import React from "react";
import { Link as RouterLink } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Chip from "@material-ui/core/Chip";
import Link from "@material-ui/core/Link";

import { i18n } from "../../translate/i18n";
import { formatDateTime, groupColors, groupOf, readableTextOn } from "./ratingHelpers";

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2)
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  tableWrapper: {
    overflowX: "auto"
  },
  rate: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  rateBadge: {
    minWidth: 32,
    height: 26,
    borderRadius: 13,
    padding: theme.spacing(0, 1),
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700
  }
}));

const RateCell = ({ row, scale, colors, classes }) => {
  if (row.status !== "answered") {
    return (
      <Chip
        size="small"
        variant="outlined"
        label={i18n.t(`ticketRatings.status.${row.status}`)}
      />
    );
  }
  const group = groupOf(row.rate, scale);
  return (
    <span className={classes.rate}>
      <span
        className={classes.rateBadge}
        style={{ backgroundColor: colors[group], color: readableTextOn(colors[group]) }}
      >
        {row.rate}
      </span>
      <Typography variant="body2" color="textSecondary" component="span">
        {i18n.t(`ticketRatings.groups.${group}.name`)}
      </Typography>
    </span>
  );
};

const RatingsResponses = ({ report, status, onStatusChange }) => {
  const classes = useStyles();
  const theme = useTheme();
  const colors = groupColors(theme);

  return (
    <Paper className={classes.paper}>
      <div className={classes.header}>
        <div>
          <Typography variant="h6" color="primary">
            {i18n.t("ticketRatings.responses.title")}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("ticketRatings.responses.subtitle")}
          </Typography>
        </div>
        <TextField
          select
          label={i18n.t("ticketRatings.filters.status")}
          value={status}
          onChange={e => onStatusChange(e.target.value)}
          style={{ minWidth: 180 }}
        >
          <MenuItem value="">{i18n.t("ticketRatings.filters.all")}</MenuItem>
          {["answered", "no_answer", "waiting"].map(s => (
            <MenuItem key={s} value={s}>
              {i18n.t(`ticketRatings.status.${s}`)}
            </MenuItem>
          ))}
        </TextField>
      </div>

      <div className={classes.tableWrapper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("ticketRatings.table.sentAt")}</TableCell>
              <TableCell>{i18n.t("ticketRatings.table.contact")}</TableCell>
              <TableCell>{i18n.t("ticketRatings.table.user")}</TableCell>
              <TableCell>{i18n.t("ticketRatings.table.connection")}</TableCell>
              <TableCell>{i18n.t("ticketRatings.table.rating")}</TableCell>
              <TableCell>{i18n.t("ticketRatings.table.answeredAt")}</TableCell>
              <TableCell align="right">{i18n.t("ticketRatings.table.ticket")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report.details.map(row => (
              <TableRow key={row.id}>
                <TableCell>{formatDateTime(row.sentAt)}</TableCell>
                <TableCell>{row.contactName || row.contactNumber || "—"}</TableCell>
                <TableCell>{row.userName || "—"}</TableCell>
                <TableCell>{row.whatsappName || "—"}</TableCell>
                <TableCell>
                  <RateCell row={row} scale={report.scale} colors={colors} classes={classes} />
                </TableCell>
                <TableCell>{formatDateTime(row.answeredAt)}</TableCell>
                <TableCell align="right">
                  {row.ticketUuid ? (
                    <Link component={RouterLink} to={`/tickets/${row.ticketUuid}`}>
                      #{row.ticketId}
                    </Link>
                  ) : (
                    "—"
                  )}
                </TableCell>
              </TableRow>
            ))}
            {report.details.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {i18n.t("ticketRatings.table.empty")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Paper>
  );
};

export default RatingsResponses;
